import React, { useEffect, useId, useRef, useState } from "react";

// Room kept free for the fixed mobile action bar when deciding to open upward.
const BOTTOM_RESERVED_PX = 96;
const MENU_MAX_HEIGHT_PX = 216;

/* Quantity picker for a cart line: a "Qty 2" button that opens a styled list.
   Works with mouse, touch and keyboard (arrows, Home/End, Enter, Escape). */
const CartQuantityDropdown = ({ value, options, label, disabled = false, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [opensUp, setOpensUp] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const wrapperRef = useRef(null);
  const buttonRef = useRef(null);
  const listRef = useRef(null);
  const optionIdPrefix = useId();

  const openMenu = () => {
    const rect = buttonRef.current?.getBoundingClientRect();
    if (rect) {
      const spaceBelow = window.innerHeight - rect.bottom - BOTTOM_RESERVED_PX;
      setOpensUp(spaceBelow < MENU_MAX_HEIGHT_PX && rect.top > spaceBelow);
    }
    setActiveIndex(Math.max(options.indexOf(value), 0));
    setIsOpen(true);
  };

  const closeMenu = (restoreFocus = true) => {
    setIsOpen(false);
    if (restoreFocus) buttonRef.current?.focus();
  };

  const selectOption = (option) => {
    if (option !== value) onChange(option);
    closeMenu();
  };

  // Close when tapping or clicking anywhere outside the picker.
  useEffect(() => {
    if (!isOpen) return undefined;
    const handlePointerDown = (event) => {
      if (!wrapperRef.current?.contains(event.target)) setIsOpen(false);
    };
    document.addEventListener("pointerdown", handlePointerDown);
    return () => document.removeEventListener("pointerdown", handlePointerDown);
  }, [isOpen]);

  // Keep the highlighted option visible and focused while navigating.
  useEffect(() => {
    if (!isOpen) return;
    listRef.current?.focus();
    listRef.current?.children[activeIndex]?.scrollIntoView({ block: "nearest" });
  }, [isOpen, activeIndex]);

  const handleButtonKeyDown = (event) => {
    if (["ArrowDown", "ArrowUp", "Enter", " "].includes(event.key)) {
      event.preventDefault();
      openMenu();
    }
  };

  const handleListKeyDown = (event) => {
    const lastIndex = options.length - 1;
    const moves = {
      ArrowDown: () => setActiveIndex((index) => Math.min(index + 1, lastIndex)),
      ArrowUp: () => setActiveIndex((index) => Math.max(index - 1, 0)),
      Home: () => setActiveIndex(0),
      End: () => setActiveIndex(lastIndex),
      Enter: () => selectOption(options[activeIndex]),
      " ": () => selectOption(options[activeIndex]),
      Escape: () => closeMenu(),
      Tab: () => closeMenu(false),
    };
    if (!moves[event.key]) return;
    if (event.key !== "Tab") event.preventDefault();
    moves[event.key]();
  };

  return (
    <div className={`cart-qty ${isOpen ? "is-open" : ""}`} ref={wrapperRef}>
      <button
        type="button"
        ref={buttonRef}
        className="cart-item__qty"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-label={`${label} ${value}`}
        aria-busy={disabled}
        disabled={disabled}
        onClick={() => (isOpen ? closeMenu() : openMenu())}
        onKeyDown={handleButtonKeyDown}
      >
        <span className="cart-item__qty-text">
          <span className="cart-item__qty-label">{label}</span>
          <span className="cart-item__qty-value">{value}</span>
        </span>
        <svg className="cart-item__qty-icon" viewBox="0 0 24 24" aria-hidden="true">
          <path d="M12 5.83 15.17 9l1.41-1.41L12 3 7.41 7.59 8.83 9zm0 12.34L8.83 15l-1.41 1.41L12 21l4.59-4.59L15.17 15z" />
        </svg>
      </button>

      {isOpen && (
        <ul
          ref={listRef}
          role="listbox"
          tabIndex={-1}
          aria-label={label}
          aria-activedescendant={`${optionIdPrefix}-${options[activeIndex]}`}
          className={`cart-qty__menu ${opensUp ? "cart-qty__menu--up" : ""}`}
          onKeyDown={handleListKeyDown}
        >
          {options.map((option, index) => {
            const isSelected = option === value;
            return (
              <li
                key={option}
                id={`${optionIdPrefix}-${option}`}
                role="option"
                aria-selected={isSelected}
                className={`cart-qty__option ${isSelected ? "is-selected" : ""} ${
                  index === activeIndex ? "is-active" : ""
                }`}
                onMouseEnter={() => setActiveIndex(index)}
                onClick={() => selectOption(option)}
              >
                <span>{option}</span>
                {isSelected && (
                  <svg className="cart-qty__check" viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M9 16.17 4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                  </svg>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default CartQuantityDropdown;
